import * as React from 'react';
import { UserInfo, initFeatureFlagsWithUserEmail } from '@web-config-app/core';
import { getUserNameAndEmail } from '../../utilities/get-user-name-and-email/get-user-name-and-email.util';

interface UserInfoContextValue {
  userInfo: UserInfo;
  isUserInfoLoaded: boolean;
}

const defaultValue: UserInfoContextValue = {
  userInfo: { firstName: '', lastName: '', email: '' },
  isUserInfoLoaded: false,
};

const UserInfoContext = React.createContext<UserInfoContextValue>(defaultValue);

/**
 * Fetches user info once and shares it with all descendants via context.
 * Place this in the protected app layout so every authorized consumer shares one fetch.
 */
export function UserInfoProvider({ children }: { children: React.ReactNode }) {
  const [userInfo, setUserInfo] = React.useState<UserInfo>(
    defaultValue.userInfo,
  );
  const [isUserInfoLoaded, setIsUserInfoLoaded] = React.useState(false);

  React.useEffect(() => {
    const getAndSetUserInfo = async () => {
      try {
        const userInfoResponse = await getUserNameAndEmail();
        if (userInfoResponse) {
          initFeatureFlagsWithUserEmail(userInfoResponse.email);
          setUserInfo(userInfoResponse);
        }
      } catch {
        // Keep defaults when user info cannot be fetched.
      } finally {
        setIsUserInfoLoaded(true);
      }
    };
    getAndSetUserInfo();
  }, []);

  const value = React.useMemo(
    () => ({ userInfo, isUserInfoLoaded }),
    [userInfo, isUserInfoLoaded],
  );

  return (
    <UserInfoContext.Provider value={value}>
      {children}
    </UserInfoContext.Provider>
  );
}

/**
 * Returns the shared user info from UserInfoProvider.
 * Without a provider, returns the default (empty / not loaded) values.
 */
export const useUserInfo = (): UserInfoContextValue => {
  const context = React.useContext(UserInfoContext);
  return context;
};
